import { useState, type FormEvent } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useApiMutation, formValues } from "@/lib/mutations";
import { useSession } from "@/lib/session";
import { Alert, Button, Card, Field, Input } from "@/components/ui";
import { GoogleButton } from "@/components/google-button";
import { PasswordInput } from "@/components/password-input";
import { AuthShell } from "./AuthShell";
import { usePageTitle } from "@/lib/title";

/**
 * Where somebody actually belongs, once the door has opened.
 *
 * **A `users` row is not by itself a member of anything.** Three kinds of account sign in here and
 * belong to no agency: a client login — a tenant, an owner or a buyer who set a password on the
 * portal (ADR-0102) — a console-only account for platform staff (ADR-0098), and somebody whose
 * membership has since been removed. All three are legitimate, which is why the API cannot refuse
 * them at this door; `/app` simply has nothing in it for them, and `AppLayout` sends an org-less
 * session to `/sign-up`, where `WrongDoor` says which door they actually opened.
 *
 * Deciding it **here** rather than letting `/app` bounce them is not tidiness. `goIn` navigates
 * after an `await`, so a redirect that has already happened gets undone by an imperative
 * `navigate("/app")` arriving late — `/app` → `/sign-up` → `/app`, and the second arrival renders
 * nothing at all, because the redirect it would need has already been performed once. That is a
 * blank screen with no error in the console, and it is what this function exists to prevent.
 */
function homeForSession(session: { org?: unknown; orgs?: unknown[] } | undefined | null): string | null {
  if (!session) return null;
  return session.org || (session.orgs?.length ?? 0) > 0 ? null : "/sign-up";
}

export function SignInPage() {
  usePageTitle("Sign in");
  const { user, org, orgs, refresh, loading } = useSession();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  /**
   * Set when the password was right but a second factor is required.
   *
   * The password is kept in memory for the second step rather than a server-side "pending login"
   * — there is nothing to steal from a page that is about to be replaced, and a half-issued
   * token sitting in a browser is one more credential that can be lifted.
   */
  const [challenge, setChallenge] = useState<{ app: boolean; email: boolean; prompt: string } | null>(null);

  const goIn = async () => {
    /* Everything cached belongs to whoever was here before — nobody, or somebody else on a shared
       machine. Dropping it costs one refetch of screens that were about to be fetched anyway, and
       it means no panel can paint a previous session's answer, or its refusal, at the new one. */
    queryClient.clear();
    await refresh();
    /* Read back from the cache rather than from `useSession()`. This runs after an `await`, so the
       values this closure captured are the ones from before the session was refetched — and the
       whole question being asked is what the *new* session says. */
    const elsewhere = homeForSession(queryClient.getQueryData(["me"]));
    if (elsewhere) {
      navigate(elsewhere, { replace: true });
      return;
    }
    /* Router state when a guard sent them here, `?from=` when `api.ts` did — a lost session is a
       full page load, and a full page load has no router state to carry. Both are checked, and
       both are required to start `/app` so the parameter cannot be used to bounce somebody
       somewhere else. */
    const asked = new URLSearchParams(location.search).get("from");
    const from = (location.state as { from?: string } | null)?.from ?? asked;
    navigate(from && from.startsWith("/app") ? from : "/app", { replace: true });
  };

  const signIn = useApiMutation({
    mutationFn: (input: { email: string; password: string }) =>
      api.postAsHuman<{ ok: boolean; twoFactor?: { app: boolean; email: boolean; prompt: string } }>("/auth/sign-in", input, "sign_in"),
    invalidates: [["me"]],
    onSuccess: async (result) => {
      if (result.twoFactor) {
        setChallenge(result.twoFactor);
        return;
      }
      await goIn();
    },
  });

  const verify = useApiMutation({
    mutationFn: (input: Record<string, unknown>) => api.postAsHuman("/auth/two-factor/verify", input, "second_factor"),
    invalidates: [["me"]],
    onSuccess: goIn,
  });

  // Already signed in, arriving at the front door again — a bookmark, or the back button.
  if (!loading && user) return <Navigate to={homeForSession({ org, orgs }) ?? "/app"} replace />;

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const values = formValues(event.currentTarget);
    setEmail(values.email ?? "");
    setPassword(values.password ?? "");
    signIn.mutate({ email: values.email, password: values.password });
  };

  return (
    <AuthShell>
      <Card>
        {challenge ? (
          <>
            <h1 className="text-xl font-semibold">One more step</h1>
            <p className="mt-1 text-sm text-ink-500">{challenge.prompt}</p>

            <form
              className="mt-5 space-y-4"
              onSubmit={(event) => {
                event.preventDefault();
                const form = new FormData(event.currentTarget);
                verify.mutate({ email, password, code: String(form.get("code") ?? "") });
              }}
            >
              {verify.errorMessage ? <Alert tone="danger">{verify.errorMessage}</Alert> : null}

              <Field
                label="Your code"
                htmlFor="code"
                required
                hint={
                  challenge.app && challenge.email
                    ? "From your authenticator app, or the one we emailed — either works."
                    : "A recovery code works here too."
                }
              >
                <Input
                  id="code"
                  name="code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  required
                  placeholder="123456"
                />
              </Field>

              <Button type="submit" disabled={verify.isPending} className="w-full">
                {verify.isPending ? "Checking…" : "Continue"}
              </Button>

              <button
                type="button"
                className="text-sm text-ink-500 hover:text-ink-800"
                onClick={() => {
                  setChallenge(null);
                  setPassword("");
                }}
              >
                Use a different account
              </button>
            </form>
          </>
        ) : (
          <>
        <h1 className="text-xl font-semibold">Sign in</h1>
        <p className="mt-1 text-sm text-ink-500">Welcome back.</p>

        <form onSubmit={submit} className="mt-5 space-y-4">
          {signIn.errorMessage ? <Alert tone="danger">{signIn.errorMessage}</Alert> : null}

          <Field label="Email address" htmlFor="email" required>
            <Input id="email" name="email" type="email" autoComplete="username" required defaultValue={email} placeholder="you@agency.com" />
          </Field>

          <Field label="Password" htmlFor="password" required>
            <PasswordInput id="password" autoComplete="current-password" required />
          </Field>

          <div className="-mt-2">
            <Link to="/forgot-password" className="link text-sm">
              Forgotten your password?
            </Link>
          </div>

          <Button type="submit" disabled={signIn.isPending} className="w-full">
            {signIn.isPending ? "Signing in…" : "Sign in"}
          </Button>
        </form>

        <p className="mt-5 text-sm text-ink-500">
          No account yet?{" "}
          <Link to="/sign-up" className="link">
            Create an Account
          </Link>
        </p>

        {/* A brand-new Google account finishes on the sign-up screen. The proof travels in
            navigation state, not the URL: a token in a query string ends up in history, in logs,
            and in whatever the browser syncs. */}
        <GoogleButton onSignedIn={goIn} onNeedsSignUp={(details) => navigate("/sign-up", { state: { google: details } })} />

          </>
        )}
      </Card>
    </AuthShell>
  );
}
